import { useEffect, useRef, useState } from 'react';
import { requestTaskDetail } from '@shakkerai/services';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { useDebounceEffect } from 'ahooks';
import { scheduleLoadImage } from '@shakkerai/pro-utils';

const loadings = {};
const cachedInfo = {};

const cachedGenerationInfo = {};
const fetchQueue = {};

export const cacheGenerationInfo = (innerTaskId, genInfo) => {
  cachedGenerationInfo[innerTaskId] = genInfo;
};

export const scheduleRequestGenerationInfo = async (innerTaskId) => {
  if (cachedGenerationInfo[innerTaskId]) {
    return cachedGenerationInfo[innerTaskId];
  }

  if (fetchQueue[innerTaskId]) {
    return fetchQueue[innerTaskId];
  }

  const promise = new Promise((resolve, reject) => {
    requestTaskDetail({ generateId: innerTaskId })
      .then((res) => res.data)
      .then(resolve)
      .catch(reject);
  });

  fetchQueue[innerTaskId] = promise;

  return promise;
};

export const fetchGenerationInfo = async (innerTaskId, innerImageId) => {
  try {
    const taskInfo = await scheduleRequestGenerationInfo(innerTaskId);
    const genInfo = taskInfo.projectData || taskInfo.taskParam?.projectData;
    const image = innerImageId ? taskInfo.images.find((item) => item.outputId === innerImageId) : taskInfo.images[0];

    if (image?.seed) {
      genInfo.acturalSeed = `${image.seed}`;
    }

    if (taskInfo.param?.prompt) {
      genInfo.acturalPrompt = taskInfo.param.prompt;
    }

    if (image?.styleCode) {
      genInfo.styleCode = image?.styleCode;
    }

    return genInfo;
  } catch (error) {
    return null;
  }
};

export const cacheElementGenInfo = (innerTaskId, genInfo) => {
  cachedInfo[innerTaskId] = genInfo;
};

export const getElementGenInfo = async (element) => {
  return await fetchGenerationInfo(element.innerTaskId, element.innerImageId);
};

export const useElementGenInfo = (element) => {
  const [genInfo, setGenInfo] = useState(cachedInfo[element?.innerTaskId] || {});

  useEffect(() => {
    if (!element?.innerTaskId) {
      setGenInfo({});
    } else {
      if (cachedInfo[element.innerTaskId]) {
        setGenInfo(cachedInfo[element.innerTaskId]);
        return;
      }

      if (loadings[element.innerTaskId]) {
        return;
      }

      loadings[element.innerTaskId] = true;

      fetchGenerationInfo(element.innerTaskId, element.innerImageId).then((genInfo) => {
        setGenInfo(genInfo);

        loadings[element.innerTaskId] = false;
        cachedInfo[element.innerTaskId] = genInfo;
      });
    }
  }, [element?.innerTaskId, element?.innerImageId]);

  return genInfo;
};

const maxRenderedImageWidths = {};
const resolutionLevels = [200, 512, 768, 1024, 1536];

// 根据图片当前的渲染尺寸和图片原始尺寸，查找最佳的图片尺寸
// 1. 如果图片原始尺寸小于渲染尺寸，直接使用原始尺寸
// 2. 如果图片原始尺寸大于渲染尺寸，找到最接近的分辨率尺寸
// 3. 如果图片原始尺寸大于渲染尺寸，但是没有找到合适的分辨率尺寸，使用原始尺寸
// 4. 从maxRenderedImageWidths中记录已经渲染过的尺寸，优先使用这个尺寸
const findBestImageWidth = (key, originalWidth, renderWidth) => {
  let result = null;

  if (originalWidth <= renderWidth) {
    // 如果图片原始尺寸小于渲染尺寸，直接使用原始尺寸
    result = originalWidth;
  } else {
    // 如果图片原始尺寸大于渲染尺寸，找到最接近的分辨率尺寸
    const matchedWidth = resolutionLevels.find((level) => level >= renderWidth);
    result = matchedWidth ? matchedWidth * 2 : originalWidth;
  }

  return (maxRenderedImageWidths[key] = Math.min(originalWidth, Math.max(maxRenderedImageWidths[key] || 0, result)));
};

export const useProgressiveImage = (element, imageRef, viewport) => {
  const [image, setImage] = useState(null);
  const loadings = useRef({});
  const loadedImages = useRef({});
  const currentImage = useRef(null);
  const key = `${element.t}-${element.url}`;

  useDebounceEffect(
    () => {
      if (imageRef.current?.isClientRectOnScreen()) {
        const originalWidth = element.width;
        const renderWidth = imageRef.current.getClientRect().width;
        const width = findBestImageWidth(key, originalWidth, renderWidth);
        const widthKey = `${key}-${width}`;

        if (loadedImages.current[widthKey]) {
          if (loadedImages.current[widthKey] !== currentImage.current) {
            setImage((currentImage.current = loadedImages.current[widthKey]));
          }

          return;
        }

        if (!loadings.current[widthKey]) {
          loadings.current[widthKey] = true;

          const isOriginalWidth = originalWidth === width;
          const imageUrl = isOriginalWidth ? element.url : convert2CompressedUrl(element.url, width);

          scheduleLoadImage(imageUrl)
            .then((image) => {
              setImage(image);

              currentImage.current = image;
              loadedImages.current[widthKey] = image;
              loadings.current[widthKey] = false;
            })
            .catch(() => {
              setImage(null);
              loadings.current[widthKey] = false;
            });
        }
      }
    },
    [key, viewport],
    { wait: 250 },
  );

  return image;
};
